import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  inject,
  InjectFlags,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Option } from '@digilize/shared/definitions/src';
import { SingleSelectSearchComponent } from '@digilize/shared/ui/components/src/lib/form-items/single-select-search/single-select-search.component';
import { forkJoin, throwError } from 'rxjs';

import { createOptions, transformToOption } from '../../../helpers/helpers';
import { CvService } from '../../../services/cv.service';
import { ErrorService } from '../../../services/error.service';
import { SkillsService } from '../../../services/skills.service';
import { WizardService } from '../../../services/wizard.service';

@Component({
  selector: 'bwc-languages-knowledge',
  templateUrl: './languages-knowledge.component.html',
  styleUrls: ['./languages-knowledge.component.scss'],
})
export class LanguagesKnowledgeComponent implements OnInit {
  @Input() prevBut: boolean;
  @Input() closeBut: boolean;
  @Input() editing: boolean;
  @Input() formGroup: FormGroup;
  @Input() removeProfileCheckClass = true;
  @ViewChild('searchAutocomplete') searchAutocomplete: SingleSelectSearchComponent;
  @Output() emitClose = new EventEmitter();
  @Output() emitLanguages = new EventEmitter();

  wizardService = inject(WizardService, InjectFlags.Optional);

  form: FormGroup;
  languagesList: Option[];
  dataLoaded = false;
  vacancyId: string;
  companyId: string;
  openFormLink: string;

  constructor(
    private fb: FormBuilder,
    public errorService: ErrorService,
    private cvService: CvService,
    private skillsService: SkillsService,
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (this.formGroup !== undefined) {
      this.form = this.createFormGroup();
      this.getDataEdit();
    } else {
      this.form = this.createFormGroup();
      this.getData();
    }
    this.checkParamsForApplying();
    if (this.removeProfileCheckClass) {
      this.renderer.removeClass(document.body, 'profile-check');
    }
  }

  createFormGroup(): FormGroup<SelectedLanguages> {
    return this.fb.group<SelectedLanguages>({
      selectedLanguages: this.fb.array<UsersLangs>([]),
    });
  }

  checkParamsForApplying() {
    this.route.queryParams.subscribe((params) => {
      const vacancyId = params['apply_to'];
      const companyId = params['c'];
      this.openFormLink = params['ofl'];
      if (vacancyId) {
        this.vacancyId = vacancyId;
      }
      if (companyId) {
        this.companyId = companyId;
      }
    });
  }

  getDataEdit(): void {
    if (this.formGroup) {
      forkJoin({
        possibleLanguages: this.skillsService.getListOfPossibleLanguages(''),
        userLanguages: this.skillsService.getLanguageSkills(),
      }).subscribe((data) => {
        const langOptions = createOptions(data.possibleLanguages.data);
        const userLangs = (data.userLanguages.data ?? []).map((lang) => ({
          skill_id: lang.skill_id,
          skill_name: lang.skill_name,
          val: lang.id,
          rating: lang.rating ?? 0,
          rating_written: lang.rating_written ?? 0,
          is_native: lang.is_native ?? false,
        }));
        this.processLanguageData(langOptions, userLangs);
      });
    }
  }

  getData(): void {
    forkJoin({
      userData: this.cvService.getCV(),
      languagesList: this.skillsService.getListOfPossibleLanguages(''),
    }).subscribe((data: any) => {
      const langOptions = createOptions(data.languagesList.data);
      const userLangs = (data.userData.language_skills ?? []).map((lang) => ({
        skill_id: lang.skill_id,
        skill_name: lang.skill_name,
        val: lang.id,
        rating: lang.rating ?? 0,
        rating_written: lang.rating_written ?? 0,
        is_native: lang.is_native ?? false,
      }));
      this.processLanguageData(langOptions, userLangs);
    });
  }

  processLanguageData(languagesList: Option[], userLanguages: UsersLangs[]): void {
    this.languagesList = languagesList.sort((a, b) => (a.text ?? '').localeCompare(b.text ?? ''));
    this.languagesList = languagesList.filter(
      (lang) => !userLanguages.some((userLang) => userLang.skill_name === lang.text)
    );

    userLanguages.forEach((item) => {
      const itemFormGroup = this.fb.group({
        skill_id: [item.skill_id],
        skill_name: [item.skill_name],
        val: [item.val],
        rating: [item.rating],
        rating_written: [item.rating_written ?? 0],
        is_native: [item.is_native],
      });

      this.selectedLanguages.push(itemFormGroup);
    });
    this.dataLoaded = true;
  }

  get selectedLanguages() {
    return this.form.get('selectedLanguages') as FormArray;
  }

  getFormControl(formControlName: string) {
    return this.form.get(formControlName) as FormControl;
  }

  goPrev() {
    if (!this.form.valid) {
      this.wizardService?.goToPrevious();
    } else {
      this.saveLanguagesData(this.selectedLanguages.value, false, true);
    }
  }

  onSubmit() {
    if (this.form.valid) {
      this.saveLanguagesData(this.selectedLanguages.value);
    }
  }

  onEdit() {
    if (this.form.valid) {
      this.saveLanguagesData(this.selectedLanguages.value, true);
    }
  }

  saveLanguagesData(data, close?, ifBack?) {
    const items = data.map((item: UsersLangs) => {
      const { val, ...rest } = item;
      return val ? { id: val, ...rest } : rest;
    });
    const requestBody = { items };
    this.skillsService.putLanguagesSkills(requestBody).subscribe(
      (resp) => {
        if (ifBack) {
          this.wizardService?.goToPrevious();
        } else if (close) {
          this.emitLanguages.emit(this.form);
          this.emitClose.emit('languages');
        } else {
          this.wizardService?.goToNext();
        }
      },
      (error) => {
        if (ifBack) {
          this.wizardService?.goToPrevious();
        }
        throwError('Languages Put error:', error);
      }
    );
  }

  handleFilteredOptionClick(option: Option) {
    const newItem = this.fb.group({
      skill_id: [option.val],
      skill_name: [option.text],
      rating: 0,
      rating_written: 0,
      is_native: false,
    });
    this.selectedLanguages.push(newItem);
    this.checkDuplicates();
  }

  checkDuplicates() {
    this.languagesList = this.languagesList.filter(
      (lang) => !this.selectedLanguages.value.some((userLang) => userLang.text === lang.text)
    );
    setTimeout(() => this.searchAutocomplete.clearSearchInput(), 0);
  }

  removeLanguage(index: number) {
    const langToAdd = transformToOption(this.selectedLanguages.at(index).value) as unknown as Option;
    this.selectedLanguages.removeAt(index);
    this.languagesList = [...this.languagesList, langToAdd];
    this.languagesList = this.sortArrayByKey(this.languagesList, 'text');
  }

  changeLangLevel(event) {
    this.selectedLanguages.at(event.index).get('rating').setValue(event.rating);
  }

  changeNative(index: number) {
    const control = this.selectedLanguages.at(index).get('is_native');
    const controlValue = control.value;

    control.setValue(!controlValue);

    if (!controlValue) {
      this.selectedLanguages.at(index).get('rating').setValue(5);
    } else {
      this.selectedLanguages.at(index).get('rating').setValue(0);
    }
  }

  changePositionInLangList(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      const control = this.selectedLanguages.at(event.previousIndex);
      this.selectedLanguages.removeAt(event.previousIndex);
      this.selectedLanguages.insert(event.currentIndex, control);
    }
  }

  close() {
    this.emitClose.emit('languages');
  }

  sortArrayByKey(arr, key: string) {
    return arr.sort((a, b) => {
      if (a[key] < b[key]) {
        return -1;
      }
      if (a[key] > b[key]) {
        return 1;
      }
      return 0;
    });
  }
}

interface SelectedLanguages {
  selectedLanguages: FormArray;
}

interface UsersLangs {
  skill_id: string;
  skill_name: string;
  val: string;
  rating: number;
  rating_written: number;
  is_native: boolean;
}
